import { useState } from 'react';
import Modal from '@/components/v2/common/Modal';
import type { TradeInQuoteT } from '@/lib/trade-in-quote';
import { TradeInInfoContent } from './TradeInInfoPanel';
import { TradeInOfferContent } from './TradeInOfferPanel';
import { TradeInSliderContent } from './TradeInSlider';

export type TradeInViewT = 'offer' | 'info' | 'widget';

/**
 * The trade-in panels (offer, "How Trade-in works", widget) in one modal that
 * swaps its content. Moving from one to the next keeps the same Dialog open:
 * two Dialogs closing and opening at once each save and restore the page's
 * scroll lock, and the second could put back the first's `overflow: hidden`
 * after both had closed, leaving the page unable to scroll.
 */
const TradeInModal = ({
  view,
  setView,
  quotes,
  onRemove,
}: {
  /** What the modal shows; null when closed. */
  view: TradeInViewT | null;
  setView: (view: TradeInViewT | null) => void;
  quotes: TradeInQuoteT[];
  onRemove: (quote: TradeInQuoteT) => void;
}) => {
  // The last view stays in the panel while it slides out.
  const [shown, setShown] = useState<TradeInViewT>(view ?? 'widget');

  if (view && view !== shown) {
    setShown(view);
  }

  const close = () => setView(null);

  return (
    <Modal
      isOpen={view !== null}
      onClose={close}
      width={shown === 'widget' ? 'md:max-w-[800px]' : undefined}
    >
      {shown === 'offer' && (
        <TradeInOfferContent
          onClose={close}
          quotes={quotes}
          onRemove={(removed) => {
            onRemove(removed);

            // Nothing left to show: close, so a later trade-in doesn't reopen it.
            if (quotes.length === 1) {
              close();
            }
          }}
          onAddMore={() => setView('widget')}
        />
      )}
      {shown === 'info' && (
        <TradeInInfoContent onClose={close} onAdd={() => setView('widget')} />
      )}
      {shown === 'widget' && <TradeInSliderContent onClose={close} />}
    </Modal>
  );
};

export default TradeInModal;
